import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { CompositionStateStore } from "../../../../store/CompositionStateStore";
import { CompositionService } from "../../../../services/CompositionService";
import { TimelineStore } from "../../../../store/TimelineStore";
import { reaction } from "mobx";
import { Clip, TransitionPointData, TransitionSideConfig } from '../Clip/Clip';
import './VideoClip.styles.scss';
import WaveformDisplay from "../../../../../librettoEditor/modules/timeline/components/WaveformDisplay/WaveformDisplay";
import {useAssetStore} from "../../../../../storage/AssetStore";
import {ProjectAssetsStore} from "../../../../../state/ProjectAssetsStore";
import {ApplicationStore} from "../../../../store/ApplicationStore";
import {ScriptLayerStore} from "../../../../store/ScriptLayerStore";
import { TransitionConfig, VisualTransitionConfig } from "../TransitionHandle";
import { CompositionEventsEnum } from "../../../../CompositionEvents";

interface VideoClipProps {
    clipId: string;
    layerId: string;
}

const normalizeState = (value: any) => String(value ?? '').toLowerCase();

const isSilentState = (value: any) => {
    const state = normalizeState(value);
    return state === 'silent' || state === 'not_needed';
};

const isTrackSilent = (track: any): boolean => {
    if (!track) return false;
    if (isSilentState(track?.transcriptionState)) return true;
    if (isSilentState(track?.enhancedTrackState)) return true;
    return false;
};

export const VideoClip: React.FC<VideoClipProps> = observer(({ clipId, layerId }) => {
    const [filename, setFilename] = useState<string | undefined>(CompositionStateStore.getClipTitle(clipId));
    const [thumbnail, setThumbnail] = useState<string | undefined>(undefined);
    const [peaks, setPeaks] = useState<Float32Array | null>(null);
    const [fadeInConfig, setFadeInConfig] = useState<TransitionConfig | undefined>(undefined);
    const [fadeOutConfig, setFadeOutConfig] = useState<TransitionConfig | undefined>(undefined);
    const [visualFadeInConfig, setVisualFadeInConfig] = useState<VisualTransitionConfig | undefined>(undefined);
    const [visualFadeOutConfig, setVisualFadeOutConfig] = useState<VisualTransitionConfig | undefined>(undefined);
    const [hasAdjacentBefore, setHasAdjacentBefore] = useState(false);
    const [hasAdjacentAfter, setHasAdjacentAfter] = useState(false);
    // derive duration from MobX so component re-renders on trims
    const { GetWaveformData, GetThumbnailUrl } = useAssetStore();
    const waveformFetchSeq = useRef(0);
    const trackId = CompositionStateStore.clipIdToAssetId?.[clipId];

    useEffect(() => {
        const disposer = reaction(
            () => ({
                filename: CompositionStateStore.getClipTitle(clipId),
            }),
            ({ filename }) => {
                setFilename(filename);
            },
            { fireImmediately: true }
        );

        return () => {
            disposer();
        };
    }, [clipId]);

    // Track Studio Sound state for waveform selection
    const [studioSoundEnabled, setStudioSoundEnabled] = useState(() => {
        const clip = CompositionService.getClipById(clipId) as any;
        return Boolean(clip?.studioSoundEnabled);
    });

    // Listen for studioSoundChanged events from the engine clip
    useEffect(() => {
        const clip = CompositionService.getClipById(clipId) as any;
        if (!clip) return;

        // Event is a CustomEvent - data is in event.detail
        const handleStudioSoundChanged = (event: any) => {
            const data = event?.detail ?? event;
            setStudioSoundEnabled(Boolean(data?.enabled));
        };

        const listenerId = clip.on?.('studioSoundChanged', handleStudioSoundChanged);

        // Initialize from current state
        if (typeof clip.studioSoundEnabled === 'boolean') {
            setStudioSoundEnabled(Boolean(clip.studioSoundEnabled));
        }

        return () => {
            if (listenerId) clip.off?.(listenerId);
        };
    }, [clipId]);

    // Fetch thumbnail (waveform handled by reaction below)
    useEffect(() => {
        if (!trackId) return;
        // Always fetch thumbnail
        (async () => {
            try {
                const thumbnailUrl = await GetThumbnailUrl({ thumbnailId: trackId, projectId: ProjectAssetsStore.projectId as string });
                setThumbnail(thumbnailUrl);
            } catch {}
        })();
    }, [clipId, trackId]);

    // Re-render when waveform becomes available (react to hasWaveform or enhancedTrackState)
    useEffect(() => {
        const seq = ++waveformFetchSeq.current;
        if (!trackId) return;
        const disposer = reaction(
            () => {
                const t = ProjectAssetsStore.projectTracks.find((x: any) => x.id === trackId || x.trackId === trackId);
                const useDenoised = studioSoundEnabled && !isTrackSilent(t);
                return {
                    hasWaveform: useDenoised
                        ? t?.enhancedTrackState === 'ready'
                        : t?.hasWaveform === true,
                    useDenoised,
                };
            },
            async ({ hasWaveform, useDenoised }) => {
                if (hasWaveform) {
                    let data: Float32Array;
                    try {
                        data = await GetWaveformData({ trackId, denoised: useDenoised });
                    } catch {
                        return;
                    }
                    if (waveformFetchSeq.current !== seq) return;
                    setPeaks(data && data.length ? data : null);
                }
            },
            { fireImmediately: true }
        );
        return () => { disposer(); };
    }, [clipId, trackId, studioSoundEnabled]);

    // Sync transition config from engine clip
    useEffect(() => {
        const clip = CompositionService.getClipById(clipId) as any;
        if (!clip) return;
        
        if (clip.fadeIn) {
            setFadeInConfig({
                type: clip.fadeIn.type ?? 'fade',
                durationSec: (clip.fadeIn.durationMs ?? 0) / 1000,
            });
        }
        if (clip.fadeOut) {
            setFadeOutConfig({
                type: clip.fadeOut.type ?? 'fade',
                durationSec: (clip.fadeOut.durationMs ?? 0) / 1000,
            });
        }

        if (clip.visualFadeIn) {
            setVisualFadeInConfig({
                type: clip.visualFadeIn.type ?? 'none',
                durationSec: (clip.visualFadeIn.durationMs ?? 0) / 1000,
                wipeDirection: clip.visualFadeIn.wipeDirection,
                blurAmount: clip.visualFadeIn.blurAmount,
            });
        }
        if (clip.visualFadeOut) {
            setVisualFadeOutConfig({
                type: clip.visualFadeOut.type ?? 'none',
                durationSec: (clip.visualFadeOut.durationMs ?? 0) / 1000,
                wipeDirection: clip.visualFadeOut.wipeDirection,
                blurAmount: clip.visualFadeOut.blurAmount,
            });
        }
    }, [clipId]);
    
    // Check for adjacent clips
    useEffect(() => {
        const clip = CompositionService.getClipById(clipId) as any;
        if (!clip || !clip.track) return;
        
        const clips = clip.track.clips ?? [];
        const myIndex = clips.findIndex((c: any) => c.id === clipId);
        if (myIndex === -1) return;
        
        const myStart = clip.start?.seconds ?? 0;
        const myEnd = (clip.start?.seconds ?? 0) + (clip.timelineDuration?.seconds ?? 0);
        
        if (myIndex > 0) {
            const prevClip = clips[myIndex - 1];
            const prevEnd = (prevClip.start?.seconds ?? 0) + (prevClip.timelineDuration?.seconds ?? 0);
            setHasAdjacentBefore(Math.abs(myStart - prevEnd) <= 0.002);
        } else {
            setHasAdjacentBefore(false);
        }
        
        if (myIndex < clips.length - 1) {
            const nextClip = clips[myIndex + 1];
            const nextStart = nextClip.start?.seconds ?? 0;
            setHasAdjacentAfter(Math.abs(nextStart - myEnd) <= 0.002);
        } else {
            setHasAdjacentAfter(false);
        }
    }, [clipId]);
    
    // Compute transition points
    const transitionPointsData = useMemo((): TransitionPointData[] => {
        const clip = CompositionService.getClipById(clipId) as any;
        if (!clip || !clip.transitionPoints || clip.transitionPoints.length === 0) {
            return [];
        }
        
        const inSec = clip.range?.[0]?.seconds ?? 0;
        const outSec = clip.range?.[1]?.seconds ?? 0;
        const clipDuration = outSec - inSec;
        if (clipDuration <= 0) return [];
        
        return clip.transitionPoints.map((tp: any) => {
            const fadeOutType = tp.transition?.fadeOut?.type ?? 'none';
            const fadeOutDur = (tp.transition?.fadeOut?.durationMs ?? 0) / 1000;
            const fadeInType = tp.transition?.fadeIn?.type ?? 'none';
            const fadeInDur = (tp.transition?.fadeIn?.durationMs ?? 0) / 1000;
            
            return {
                id: tp.id,
                position: Math.max(0, Math.min(1, (tp.srcTimeSec - inSec) / clipDuration)),
                fadeOut: { type: fadeOutType, durationSec: fadeOutDur },
                fadeIn: { type: fadeInType, durationSec: fadeInDur },
            };
        });
    }, [clipId, CompositionStateStore.clips?.[clipId]]);
    
    const handleFadeInChange = useCallback((config: TransitionConfig) => {
        setFadeInConfig(config);
        const clip = CompositionService.getClipById(clipId) as any;
        if (clip) {
            clip.fadeIn = { type: config.type, durationMs: config.durationSec * 1000 };
        }
    }, [clipId]);
    
    const handleFadeOutChange = useCallback((config: TransitionConfig) => {
        setFadeOutConfig(config);
        const clip = CompositionService.getClipById(clipId) as any;
        if (clip) {
            clip.fadeOut = { type: config.type, durationMs: config.durationSec * 1000 };
            
            if (config.type === 'crossfade' && hasAdjacentAfter) {
                const clips = clip.track?.clips ?? [];
                const myIndex = clips.findIndex((c: any) => c.id === clipId);
                if (myIndex >= 0 && myIndex < clips.length - 1) {
                    const nextClip = clips[myIndex + 1];
                    nextClip.fadeIn = { type: 'crossfade', durationMs: config.durationSec * 1000 };
                }
            }
        }
    }, [clipId, hasAdjacentAfter]);
    
    // Visual transition handlers
    const handleVisualFadeInChange = useCallback((config: VisualTransitionConfig) => {
        setVisualFadeInConfig(config);
        const clip = CompositionService.getClipById(clipId) as any;
        if (clip) {
            clip.visualFadeIn = {
                type: config.type,
                durationMs: config.type === 'none' ? 0 : config.durationSec * 1000,
                wipeDirection: config.wipeDirection,
                blurAmount: config.blurAmount,
            };
            try { clip.trigger?.('frame', undefined); } catch {}
            try { clip.track?.composition?.trigger?.(CompositionEventsEnum.CLIP_UPDATED, { clip }); } catch {}
        }
    }, [clipId]);
    
    const handleVisualFadeOutChange = useCallback((config: VisualTransitionConfig) => {
        setVisualFadeOutConfig(config);
        const clip = CompositionService.getClipById(clipId) as any;
        if (clip) {
            clip.visualFadeOut = {
                type: config.type,
                durationMs: config.type === 'none' ? 0 : config.durationSec * 1000,
                wipeDirection: config.wipeDirection,
                blurAmount: config.blurAmount,
            };
            try { clip.trigger?.('frame', undefined); } catch {}
            try { clip.track?.composition?.trigger?.(CompositionEventsEnum.CLIP_UPDATED, { clip }); } catch {}
        }
    }, [clipId]);
    
    // Handle transition point config change for a specific side
    const handleTransitionPointConfigChange = useCallback((
        tpId: string, 
        side: 'fadeOut' | 'fadeIn', 
        config: TransitionSideConfig
    ) => {
        const clip = CompositionService.getClipById(clipId) as any;
        if (clip && clip.setTransitionPointConfig) {
            const tp = clip.transitionPoints?.find((t: any) => t.id === tpId);
            const currentTransition = tp?.transition ?? {};
            
            const newTransition = {
                ...currentTransition,
                [side]: {
                    type: config.type,
                    durationMs: config.durationSec * 1000,
                }
            };
            
            clip.setTransitionPointConfig(tpId, newTransition);
        }
    }, [clipId]);
    
    // Handle transition point removal
    const handleTransitionPointRemove = useCallback((tpId: string) => {
        const clip = CompositionService.getClipById(clipId) as any;
        if (clip && clip.removeTransitionPoint) {
            clip.removeTransitionPoint(tpId);
        }
    }, [clipId]);

    const duration = CompositionStateStore.clips?.[clipId]?.trimmedDuration ?? 0;
    const clipGain = ScriptLayerStore.getClipGain(clipId);
    const engineClipForPeaks: any = CompositionService.getClipById(clipId);
    const clipWindowKey = `${engineClipForPeaks?.range?.[0]?.seconds ?? 0}:${engineClipForPeaks?.range?.[1]?.seconds ?? 0}:${engineClipForPeaks?.playbackRate ?? 1}:${Array.isArray(engineClipForPeaks?.cuts) ? engineClipForPeaks.cuts.length : 0}`;

    // Compute displayPeaks.
    // IMPORTANT: memoize so WaveSurfer isn't destroyed/re-created on every render (e.g. when selecting a range).
    const displayPeaks = useMemo(() => {
        if (!peaks || !peaks.length) return null;
        const clip: any = engineClipForPeaks;
        if (!clip) return peaks;
        const mediaDur = clip.duration?.seconds ?? 0;
        if (!mediaDur) return peaks;
        const segs: Array<{ srcStartSec: number; srcEndSec: number; compStartSec: number }> = clip.segments ?? [];
        if (Array.isArray(segs) && segs.length > 0) {
            const parts: Float32Array[] = [];
            let total = 0;
            for (const seg of segs) {
                const s = Math.max(0, Math.min(mediaDur, seg.srcStartSec ?? 0));
                const e = Math.max(0, Math.min(mediaDur, seg.srcEndSec ?? 0));
                if (e <= s) continue;
                const i0 = Math.max(0, Math.floor((s / mediaDur) * peaks.length));
                const i1 = Math.min(peaks.length, Math.floor((e / mediaDur) * peaks.length));
                if (i1 > i0) {
                    const sub = peaks.subarray(i0, i1);
                    parts.push(sub);
                    total += sub.length;
                }
            }
            if (!parts.length) return new Float32Array();
            const out = new Float32Array(total);
            let cursor = 0;
            for (const part of parts) {
                out.set(part, cursor);
                cursor += part.length;
            }
            return out;
        }
        // Fallback to trimmed outer span
        const baseIn = clip.range?.[0]?.seconds ?? 0;
        const baseOut = clip.range?.[1]?.seconds ?? mediaDur;
        const inSec = Math.min(baseOut, Math.max(0, baseIn));
        const outSec = Math.max(inSec, Math.min(mediaDur, baseOut));
        if (outSec <= inSec) return new Float32Array();
        const startIdx = Math.max(0, Math.floor((inSec / mediaDur) * peaks.length));
        const endIdx = Math.min(peaks.length, Math.floor((outSec / mediaDur) * peaks.length));
        return peaks.subarray(startIdx, Math.max(startIdx, endIdx));
    }, [clipId, peaks, duration, clipWindowKey]);

    const shouldExpectWaveform = useMemo(() => {
        const clip: any = CompositionService.getClipById(clipId);
        if (clip?.hasAudioStream === false) return false;
        if (clip?.hasAudioStream === true) return true;

        if (!trackId) return true;
        const track = ProjectAssetsStore.projectTracks.find((x: any) => x.id === trackId || x.trackId === trackId);
        if (!track) return true;
        if (track?.hasAudioStream === false) return false;

        const stateLower = String(track?.transcriptionState || '').toLowerCase();
        if (stateLower === 'not_needed' || stateLower === 'silent') return false;
        return true;
    }, [clipId, trackId, ProjectAssetsStore.projectTracks]);

    const silentPeaks = useMemo(() => {
        if (shouldExpectWaveform) return null;
        if (duration <= 0) return null;
        const peaksPerSecond = 40;
        const length = Math.max(1, Math.ceil(duration * peaksPerSecond));
        return new Float32Array(length);
    }, [shouldExpectWaveform, duration]);

    const finalPeaks = displayPeaks || silentPeaks;

    // Check if waveform is processing only when one is actually expected to arrive.
    const isProcessing = shouldExpectWaveform && !finalPeaks && duration > 0;
    const selectedRange =
        ApplicationStore.selectedClipId === clipId ? ApplicationStore.selectedRange : null;

    return (
        <Clip
            clipId={clipId}
            layerId={layerId}
            filename={filename}
            thumbnail={thumbnail}
            backgroundColor={"191, 221, 232"}
            clipVariant="media"
            clipKind="video"
            showAudioTransitions={true}
            showVisualTransitions={true}
            fadeInConfig={fadeInConfig}
            fadeOutConfig={fadeOutConfig}
            visualFadeInConfig={visualFadeInConfig}
            visualFadeOutConfig={visualFadeOutConfig}
            hasAdjacentBefore={hasAdjacentBefore}
            hasAdjacentAfter={hasAdjacentAfter}
            onFadeInChange={handleFadeInChange}
            onFadeOutChange={handleFadeOutChange}
            onVisualFadeInChange={handleVisualFadeInChange}
            onVisualFadeOutChange={handleVisualFadeOutChange}
            transitionPoints={transitionPointsData}
            onTransitionPointConfigChange={handleTransitionPointConfigChange}
            onTransitionPointRemove={handleTransitionPointRemove}
        >
            {finalPeaks && duration > 0 && (
                <div style={{ position: 'absolute', inset: 0, opacity: 0.78, zIndex: 2 }}>
                    <WaveformDisplay
                        clipId={clipId}
                        peaks={finalPeaks}
                        duration={duration}
                        isAudioClip={false}
                        rangeEnabled={ApplicationStore.rangeToolEnabled}
                        selectedRange={selectedRange}
                        waveformColor="rgba(30, 48, 66, 0.86)"
                        gain={clipGain}
                    />
                </div>
            )}
            {isProcessing && (
                <div style={{
                    position: 'absolute',
                    top: '4px',
                    left: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '2px 6px',
                    backgroundColor: 'rgba(55, 65, 81, 0.85)',
                    borderRadius: '4px',
                    zIndex: 10,
                }}>
                    <div style={{
                        width: '10px',
                        height: '10px',
                        border: '2px solid rgba(255, 255, 255, 0.3)',
                        borderTopColor: '#fff',
                        borderRadius: '50%',
                        animation: 'clip-spin 1s linear infinite',
                    }} />
                    <span style={{
                        color: '#fff',
                        fontSize: '10px',
                        fontWeight: 500,
                    }}>Processing...</span>
                </div>
            )}
            <style>{`
                @keyframes clip-spin {
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </Clip>
    );
});

export default VideoClip;
